import type { DomainCheck } from "@onestore/api/domainSearch"
import type { FormErrors } from "@onestore/api/forms"
import type { ProductAlias } from "@onestore/api/types"
import {
  areRequestsPending,
  isResponsePending,
} from "@onestore/onestore-store-common/http"
import type { UpsellDomainsProducts } from "@gatsby-plugin-bonus/types"
import { first, get } from "lodash"
import { createSelector } from "reselect"
import type { ProductBoxBundle } from "~/fragments/bundle"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import { getHookPlans, hasHostingHookEnabled } from "~/lib/config"
import Storage from "~/lib/storage"
import {
  getBasketItems,
  getBasketToken,
  getFlatBasketItems,
} from "../basket/selectors"
import { DOMAIN_STATUS } from "../domain-search/constants"
import type { DomainSearchBundleState } from "../domain-search/selectors"
import type { AppState } from "../reducer"
import { getUserDataReady } from "../selectors"
import type { BonusProduct, BonusState } from "./types"

export const initialState: BonusState = {
  loaded: false,
  product: undefined,
  upgrade: undefined,
  bundled: undefined,
  calculation: {
    product: undefined,
    bundled: undefined,
  },
  domain: undefined,
  special: [],
  forms: {},
  upsell: {},
  upsellDomains: {
    results: [],
    status: 0,
  },
  promoCode: undefined,
  chosenPeriodName: undefined,
  hidePeriods: undefined,
  checkoutMode: "basket",
  domainsSearch: {},
  hideUpsell: false,
  emailHack: {
    enabled: false,
    emailHackPlanId: undefined,
    basketText: undefined,
  },
  axDrawer: undefined,
  miniCartOpen: false,
  backToShopUrl: undefined,
}

/**
 * Zwraca definicje bonusów.
 */
export const getDefinitions = createSelector(
  (state: AppState): BonusState => state.bonus,
  (bonus: BonusState): BonusState => bonus
)

/**
 * Zwraca głowny produkt przed-koszyka.
 */
export const getProduct = createSelector(
  (state: AppState): BonusState => state.bonus,
  (bonus: BonusState): BonusProduct | undefined => bonus.product
)

export const hasProduct = createSelector(
  (state: AppState): BonusState => state.bonus,
  (bonus: BonusState): boolean => !!bonus.product
)

export const getProductAlias = createSelector(
  getProduct,
  (product) => product?.alias
)

/**
 * Zwraca głowny produkt przed-koszyka.
 *
 * @param {Object} state Stan aplikacji
 */
export const getUpsellProductFromBasket = createSelector(
  getBasketItems,
  (state) => getProduct(state)?.basketItemState?.id,
  (items, upsellProductId) =>
    first(items.filter((item) => item.id === upsellProductId))
)

/**
 * Zwraca zbundlowany produkt przed-koszyka.
 */
export const getBundled = createSelector(
  (state: AppState) => state.bonus,
  (bonus): BonusProduct | undefined => bonus.bundled
)

export const getPriceCalculations = createSelector(
  (state: AppState) => state.bonus,
  (bonus): BonusState["calculation"] => bonus.calculation
)

/**
 * Zwraca upsell produktu o danym aliasie.
 */
export const getUpsellProduct = createSelector(
  (state: AppState, alias: string): BonusProduct =>
    get(state, ["bonus", "upsell", alias]),
  (upsell: BonusProduct): BonusProduct => upsell
)

export const getDomain = createSelector(
  (state) => get(state, ["bonus", "domain"]),
  (results) => results
)

export const getDomains = createSelector(
  (state) => get(state, ["bonus", "domainsSearch"]),
  (results) => results
)

export const getDomainsNextStep = createSelector(
  (state: AppState): boolean =>
    get(state, ["bonus", "domainsSearch", "nextStep"], false),
  (results: boolean): boolean => results
)

export const getDomainsHideSearch = createSelector(
  (state: AppState): boolean =>
    get(state, ["bonus", "domainsSearch", "hideSearch"], false),
  (results: boolean): boolean => results
)

export const getDomainsHideUpsell = createSelector(
  (state: AppState): boolean | undefined => get(state, ["bonus", "hideUpsell"]),
  (results: boolean): boolean => results
)

export const getUpsellDomains = createSelector(
  (state) => get(state, ["bonus", "upsellDomains"]),
  (results) => results
)

export const getUpsellDomainsResultsList = (state) =>
  get(state, ["bonus", "upsellDomains", "results"], [])

export const getDomainsResultsList = (state): DomainCheck.Result[] => {
  const results = get(state, ["bonus", "domainsSearch", "results"], [])

  return [...results]
}

/**
 * Zwraca informacje o bundle domenowym
 */
export const getDomainsBundles = (
  state: AppState & AppState
): DomainSearchBundleState[] => {
  const bundleDomains = get(
    state,
    ["bonus", "domainsSearch", "bundleDomains"],
    null
  )

  if (!bundleDomains) {
    return []
  }

  return bundleDomains.map((bundle) => ({
    ...bundle,
    buttonId: `domains-bundle-${bundle.id}`,
    isLoading: isResponsePending(
      state.basket.buttons[`domains-bundle-${bundle.id}`]
    ),
    isPartialBundleInBasket: bundle.domains.some((domain) =>
      state.basket.domains.includes(domain.fqdn)
    ),
    isWholeBundleInBasket: bundle.domains.every((domain) =>
      state.basket.domains.includes(domain.fqdn)
    ),
  }))
}

/**
 * Zwraca informacje o wyszukiwanych domenach w upsellu domenowym.
 * Dodatkowo sortuje wyniki wyszukiwania tak by domeny dostępne pojawiały się wyżej.
 */
export const getUpsellDomainResults = createSelector(
  getUpsellDomainsResultsList,
  (results) =>
    results.slice(0).sort((a, b) => {
      if (a.status === b.status) {
        return 0
      }

      if (a.status === DOMAIN_STATUS.AVAILABLE) {
        return -1
      }

      if (b.status === DOMAIN_STATUS.AVAILABLE) {
        return 1
      }

      return 0
    })
)

export const getDomainResults = createSelector(
  getDomainsResultsList,
  (results) =>
    results.sort((a, b) => {
      if (a.status === b.status) {
        return 0
      }

      if (a.status === DOMAIN_STATUS.AVAILABLE) {
        return -1
      }

      if (b.status === DOMAIN_STATUS.AVAILABLE) {
        return 1
      }

      return 0
    })
)
/**
 * Zwraca informacje o wprowadzonej frazie w upsellu domenowym.
 */
export const getUpsellDomainPhrase = createSelector(
  (state) => getUpsellDomains(state),
  (results) => results.phrase
)

export const getDomainPhrase = createSelector(
  (state) => getDomains(state),
  (results) => results.phrase
)

/**
 * Zwraca informacje o statusie zapytania HTTP.
 */
export const getUpsellDomainsRequestStatus = createSelector(
  (state) => getUpsellDomains(state),
  (results) => results.status
)

export const getDomainsRequestStatus = createSelector(
  (state) => getDomains(state),
  (results) => results.status
)

export const getBonusPromoCode = createSelector(
  (state: AppState): BonusState => state.bonus,
  (bonus: BonusState): string => bonus.promoCode || ""
)

export const getHideBonusPeriods = createSelector(
  (state: AppState): BonusState => state.bonus,
  (bonus: BonusState): boolean => bonus.hidePeriods || false
)

export type ParametersFormData = Record<
  string,
  string | number | boolean | null
>

export function getBonusParametersFormValues(alias: ProductAlias): {
  (state: AppState): Record<string, string>
} {
  return (state: AppState) => get(state, `bonus.forms.${alias}.values`, {})
}

export function isBonusParameterFormValid(alias: ProductAlias): {
  (state: AppState): boolean
} {
  return (state: AppState) => get(state, `bonus.forms.${alias}.valid`, true)
}

export function getBonusParametersFormExternalErrors(errorPath: string) {
  return (state: AppState): FormErrors | undefined =>
    get(state, `bonus.${errorPath}.addToBasketError`, undefined)
}

export function getAdvancedProductsAliases({ bonus }: AppState): string[] {
  const aliases: string[] = []

  if (bonus?.product?.alias && bonus?.product?.advancedConfiguration) {
    aliases.push(bonus?.product?.alias)
  }

  Object.values(bonus.upsell).forEach((upsell) => {
    if (upsell?.advancedConfiguration) {
      aliases.push(upsell?.alias)
    }
  })

  return aliases
}

export type BonusContextData = {
  ready: boolean
  definitions: BonusState
  product: BonusProduct | undefined
}

export function getBonusContextData(state: AppState): BonusContextData {
  const userDataReady = getUserDataReady(state)
  const product = getProduct(state)
  const token = getBasketToken(state)
  const definitions = getDefinitions(state)

  return {
    ready: userDataReady === true && token !== null && state.bonus.loaded,
    definitions,
    product,
  }
}

export function getBonusHookVisible(state: AppState): boolean {
  const definitions = getDefinitions(state)

  if (!hasHostingHookEnabled()) {
    return false
  }

  return (
    Object.hasOwnProperty.call(definitions, "special") &&
    definitions.special !== undefined &&
    definitions.special.indexOf("DOMAINS") > -1
  )
}

export function hasHookItemsInBasket(state: AppState) {
  const basketItems = getFlatBasketItems(state)

  return basketItems.some((item) => getHookPlans().includes(item.plan_id))
}

export function isBundleLoading(state: AppState): boolean {
  const bundle = getBundled(state)

  if (!bundle) {
    return true
  }

  return areRequestsPending([
    bundle.addToBasketStatus,
    bundle.changePeriodStatus,
    bundle.removeFromBasketStatus,
  ])
}

export function isEmailHackInBasket(state: AppState): boolean {
  const basketItems = getFlatBasketItems(state)

  return basketItems.some(
    (item) => item.plan_id === state.bonus.emailHack?.emailHackPlanId
  )
}

export const isAxDrawerOpen = (state: AppState): boolean =>
  state.bonus.axDrawer !== undefined

// Plain reads, not createSelector: memoising on the whole `bonus` slice
// recomputes on every change to it anyway, and each of these just reads a
// property, so the reference is already stable.
export const getBonusAxDrawerSaleConfiguration = (
  state: AppState
): SaleConfiguration | undefined => state.bonus.axDrawer?.saleConfiguration

export const getBonusAxDrawerBundle = (
  state: AppState
): ProductBoxBundle | undefined => state.bonus.axDrawer?.bundle

export const getBonusAxDrawerBundleProduct = (
  state: AppState
): BonusProduct | undefined => state.bonus.axDrawer?.bundleProduct

export const isBonusAxDrawerBundleSelected = (state: AppState): boolean =>
  state.bonus.axDrawer?.isBundleSelectedInDrawer ?? false

export function getAxDrawerSelectedUpsell(upsell: UpsellDomainsProducts) {
  return Object.values(upsell).filter((product) => product.isSelectedInDrawer)
}

export const getAxDrawerSelectedUpsellSelector = createSelector(
  (state: AppState) => state.bonus.upsell,
  (upsell) => getAxDrawerSelectedUpsell(upsell)
)
export const isCpShopMiniCartOpen = (state: AppState): boolean =>
  state.bonus.miniCartOpen ?? false

export const getBackToShopUrl = (state: AppState): string =>
  state.bonus.backToShopUrl ?? Storage.getBackToShopUrl() ?? "/"
